import React, { useCallback, useEffect, useState } from 'react';
import { SwiperProps } from 'swiper/react';

export type Tab<T = string | null> = {
  id: T;
  label: string | React.ReactNode | (() => string | React.ReactNode);
  hidden?: boolean;
  disabled?: boolean;
  enabled?: boolean;
  count?: string | number;
};

export type TabsProps<T extends Tab> = SwiperProps & {
  tabs: ReadonlyArray<T>;
  activeTab?: T['id'] | Array<T['id']> | null;
  onTabChange: (tab: T['id']) => void;
  allowReset?: boolean;
  swipable?: boolean;
  tabClassName?: string;
  activeTabClassName?: string;
  tabTestAttr?: string;
  defaultTabId?: T['id'] | null;
  'data-test'?: string;
  disabled?: boolean;
  onBlur?: () => void;
};

export const useTabs = <T extends Tab>(props: TabsProps<T>) => {
  const {
    tabs = [],
    activeTab: externalActiveTab,
    allowReset,
    onTabChange,
    defaultTabId = tabs[0]?.id,
    disabled,
  } = props;

  const [activeTab, setActiveTab] = useState<T['id'] | Array<T['id']> | null>(
    externalActiveTab ?? defaultTabId ?? tabs[0]?.id,
  );

  useEffect(() => {
    if (externalActiveTab !== undefined && externalActiveTab !== activeTab) {
      setActiveTab(externalActiveTab);
    }
  }, [externalActiveTab, activeTab]);

  const handleTabClick = useCallback(
    (id: T['id']) => {
      if (disabled) return;
      const newActiveTab = allowReset && id === activeTab ? null : id;
      onTabChange(newActiveTab);
      setActiveTab(newActiveTab);
    },
    [activeTab, allowReset, disabled, onTabChange],
  );

  return { activeTab, handleTabClick };
};
